import { Component, OnChanges, Input, output, inject } from '@angular/core';
import { ECharts, EChartsOption } from 'echarts';
import { ChartService, Chart, ZoomState } from '../chart.service';
import { ChartOverlayService } from '../chart-overlay.service';
import { ChartOverlayData } from '../chart-overlay.models';
import { Omit } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { ModalRef } from '@schenck/rotec-components';
import { CHART_DARK_THEME } from '../chart.themes';
import { AsyncPipe } from '@angular/common';
import { NgxEchartsModule } from 'ngx-echarts';
import { TranslateDirective } from '@ngx-translate/core';

@Component({
  selector: 'mc-chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.less'],
  imports: [NgxEchartsModule, TranslateDirective, AsyncPipe],
})
export class ChartComponent implements OnChanges {
  private readonly chartService = inject(ChartService);
  private readonly chartOverlayService = inject(ChartOverlayService);

  @Input() public chart!: Chart;
  @Input() public chartOverlay?: Omit<ChartOverlayData, 'chart'>;
  @Input() public darkMode = false;
  public readonly configChange = output<ChartOverlayData>();

  public eChart!: ECharts;
  public zoomState?: ZoomState;
  public selectedState?: any;
  public darkTheme = CHART_DARK_THEME;

  public isEmpty$ = new BehaviorSubject<boolean>(false);
  public chart$: BehaviorSubject<Chart | undefined> = new BehaviorSubject(this.getNewChart());

  public chartOverlayData$: BehaviorSubject<ChartOverlayData | undefined> = new BehaviorSubject(
    this.getChartConfigForOverlay()
  );

  public options$: Observable<EChartsOption[]>;

  public onChartInit(chart: ECharts): void {
    this.eChart = chart;
    this.eChart.on('datazoom', (p: any) => {
      if (p.batch) {
        this.zoomState = { start: p.batch[0].start, end: p.batch[0].end };
        this.configChanged();
      }
    });

    this.eChart.on('legendselectchanged', (p: any) => {
      this.selectedState = p.selected;
      this.configChanged();
    });
  }

  public ngOnChanges(): void {
    const newChart = this.getNewChart();

    if (this.isChartEmpty(newChart)) {
      this.isEmpty$.next(true);
      return;
    } else {
      this.isEmpty$.next(false);
    }

    this.chart$.next(newChart);
    this.chartOverlayData$.next(this.getChartConfigForOverlay());
    this.options$ = this.chartService.getChartOptions(this.chart$, this.darkMode);
  }

  public async openOverlay(evt: MouseEvent): Promise<void> {
    if (this.chartOverlay && !this.isEmpty$.value) {
      const chartHeight = this.chart.height || 290;
      const bottom = chartHeight - evt.offsetY;
      // Don't open chart overlay when clicking too much at the bottom to prevent
      // opening the overlay when the user wanted to interact with the legend instead
      if (bottom > 80) {
        // update chart options when something in the overlay changes
        const modalRef: ModalRef = await this.chartOverlayService.openChartOverlay(
          this.chartOverlayData$
        );

        const uns = modalRef.data!.subscribe((chartConfig) => {
          if (typeof chartConfig?.chart?.config?.selectedPlane === 'number') {
            this.updateChartOptions(chartConfig);
          }
        });

        modalRef.dispose$.subscribe(() => {
          uns.unsubscribe();
        });
      }
    }
  }

  private isChartEmpty(chart: Chart | undefined): boolean {
    switch (chart.chartType) {
      case 'simplebar':
        return this.isSimpleBarChartEmpty(chart);
      case 'barline':
        return this.isBarLineChartEmpty(chart);
      case 'polar':
        return this.isPolarChartEmpty(chart);
      case 'line':
        return this.isLineChartEmpty(chart);
      case 'multitrend':
        return this.isMultitrendChartEmpty(chart);
      default:
        return false;
    }
  }

  /**
   * A simple barchart is empty, if either one of the conditions match:
   *
   * 1. no xAxis values exists (or the length of the xAxis values is 0),
   * 2. All yAxis value arrays have 0 length
   * 3. or all values of the yAxis are 0
   */
  private isSimpleBarChartEmpty(chart: Chart): boolean {
    const noXAxisValues = chart.data?.xAxis?.values?.length === 0;
    const allYAxisAreEmptyOrZero = () =>
      chart.data.measures.every((measure) => {
        const isEmpty = !measure.yAxisData.values.length;
        const allValuesZero = () => measure.yAxisData.values.every((value) => value === 0);
        return isEmpty || allValuesZero();
      });

    return noXAxisValues || allYAxisAreEmptyOrZero();
  }

  private isBarLineChartEmpty(chart: Chart): boolean {
    const noXAxisValues = chart.data?.xAxis?.values?.length === 0;
    const yAxisEmpty = () =>
      chart.data.measures.every((measure) => {
        const allYAxisAreEmpty = !measure.yAxisData.length;
        const yAxisValuesAreEmpty = () => measure.yAxisData.every((data) => !data.values.length);
        const yAxisValuesAreZero = () =>
          measure.yAxisData.every((data) => data.values.every((value) => value === 0));

        return allYAxisAreEmpty || yAxisValuesAreEmpty() || yAxisValuesAreZero();
      });

    return noXAxisValues || yAxisEmpty();
  }

  private isPolarChartEmpty(chart: Chart): boolean {
    return !chart.data?.data?.main?.data || chart.data?.data?.main?.data.length === 0;
  }

  private isLineChartEmpty(chart: Chart): boolean {
    const noXAxisValues = chart.data?.xAxis?.length === 0;

    const yAxisEmpty = chart.data.measures.every((mesaure) => {
      const isEmpty = !mesaure.yAxisData.values.length;
      const allZero = mesaure.yAxisData.values.every((value) => value === 0);
      return isEmpty || allZero;
    });

    return noXAxisValues || yAxisEmpty;
  }

  private isMultitrendChartEmpty(chart: Chart): boolean {
    return !chart.data?.main?.data || chart.data?.main?.data.length === 0;
  }

  private configChanged(): void {
    this.chart$.next(this.getNewChart());
    this.chartOverlayData$.next(this.getChartConfigForOverlay());
    this.configChange.emit(this.getChartConfigForOverlay());
  }

  private updateChartOptions(chartOverlayData: ChartOverlayData | undefined): void {
    if (chartOverlayData) {
      const config = chartOverlayData.chart.config || {};
      const _chart = {
        chartType: chartOverlayData.chart.chartType,
        data: chartOverlayData.chart.data,
        height: chartOverlayData.chart.height,
        config: config
          ? {
              truncate: true,
              zoom: config.zoom !== undefined ? config.zoom : undefined,
              zoomState: config.zoom !== undefined ? config.zoomState : undefined,
              percentAxis: config.percentAxis !== undefined ? config.percentAxis : undefined,
              selectedPlane: config.selectedPlane ? config.selectedPlane : undefined,
              selectedState: config.selectedState ? config.selectedState : undefined,
            }
          : {},
      };
      this.zoomState = config.zoomState;
      this.selectedState = config.selectedState;
      this.chart$.next(_chart);
    }
  }

  private getNewChart(): Chart | undefined {
    return this.chart
      ? {
          ...this.chart,
          config: this.chart.config
            ? {
                zoom: this.chart.config.zoom,
                truncate: this.chart.config.truncate,
                zoomState: this.chart.config.zoomState
                  ? this.chart.config.zoomState
                  : this.zoomState,
                selectedState: this.chart.config.selectedState
                  ? this.chart.config.selectedState
                  : this.selectedState,
                selectedPlane: this.chart.config.selectedPlane
                  ? this.chart.config.selectedPlane
                  : 0,
                percentAxis: this.chart.config.percentAxis,
              }
            : {
                zoom: true,
                truncate: true,
                zoomState: this.zoomState,
                selectedState: this.selectedState,
                selectedPlane: 0,
                percentAxis: false,
              },
        }
      : undefined;
  }

  private getChartConfigForOverlay(): ChartOverlayData | undefined {
    return this.chartOverlay
      ? {
          title: this.chartOverlay.title,
          legendData: this.chartOverlay.legendData,
          chart: {
            ...this.chart,
            // don't add height because there is enough space in the overlay
            height: undefined,
            config: this.chart.config
              ? {
                  ...this.chart.config,
                  // don't truncate because there is enough space in the overlay
                  truncate: false,
                  zoomState: this.zoomState,
                  selectedState: this.selectedState,
                  selectedPlane: this.chart.config.selectedPlane
                    ? this.chart.config.selectedPlane
                    : undefined,
                }
              : {
                  truncate: false,
                  zoomState: this.zoomState,
                  selectedState: this.selectedState,
                },
          },
        }
      : // if there is no special overlayConfig needed but we still want to save the zoom and selected state (i.e. in the pie chart)
        this.chart
        ? {
            title: '',
            legendData: {
              asset: '',
              compareAsset: '',
              showCircle: true,
            },
            chart: {
              ...this.chart,
              // don't add height because there is enough space in the overlay
              height: undefined,
              config: this.chart.config
                ? {
                    ...this.chart.config,
                    // don't truncate because there is enough space in the overlay
                    truncate: false,
                    zoomState: this.zoomState,
                    selectedState: this.selectedState,
                  }
                : {
                    truncate: false,
                    zoomState: this.zoomState,
                    selectedState: this.selectedState,
                  },
            },
          }
        : undefined;
  }
}
